import { SchedulerItem, SchedulerItemContent } from '@progress/kendo-react-scheduler';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ApplicationConfigurationKeys, COLUMNSTYPE, MEDIACATEGORIES, MODULE, TX_MODE, TX_STATUS } from '../../../framework/constant/constant';
import { utility } from '../../../framework/utility/utilityProvider';
import { PlanningHelper } from '../helper/PlanningHelper';
import moment from 'moment';

// Status Icons Component - extracted for cleaner code
const StatusIcons = React.memo(({
  isPlanningItemLocked, isImportant, showFormat, showNoSegment,
  showContract, showRecursive, showInvalidFormat, isInvalidSegmentSelected,
  isChanged, txStatus, hasAnyIcon, backgroundColor, isCatchUp, isBlackOut
}) => {
  if (!hasAnyIcon) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '10px',
      padding: '3px 6px',
      backgroundColor: backgroundColor ?? '#f8f9fa',
      borderTop: '1px solid rgba(0,0,0,0.1)',
      zIndex: 2,
      pointerEvents: 'none',
      borderRadius: '0 0 4px 4px'
    }}>
      {/* Left side icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {isPlanningItemLocked && (
          <i className="fa fa-lock" style={{ fontSize: '12px', color: '#dc3545' }} title="Locked" />
        )}
      </div>

      {/* Right side icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {isImportant && (
          <i className="fa fa-exclamation-triangle" style={{ fontSize: '11px', color: '#ffc107' }} title="Important" />
        )}
        {showFormat && (
          <i className="fa fa-cube" style={{ fontSize: '11px' }} title="Format" />
        )}
        {showNoSegment && (
          <i className="fa fa-window-close" style={{ fontSize: '11px', color: '#dc3545' }} title="No segments" />
        )}
        {showContract && (
          <i className="fa fa-pencil-square" style={{ fontSize: '11px' }} title="Contract" />
        )}
        {showRecursive && (
          <i className="fa fa-bars" style={{ fontSize: '11px' }} title="Recursive" />
        )}
        {showInvalidFormat && isInvalidSegmentSelected && (
          <i className="fa fa-triangle-exclamation" style={{ fontSize: '11px', color: '#dc3545' }} title="Invalid format" />
        )}
        {isChanged && (
          <i className="fa fa-refresh" style={{ fontSize: '11px' }} title="Changed" />
        )}
        {txStatus.SID === TX_STATUS.Live && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            padding: '1.5px 2px 0px',
            backgroundColor: '#dc3545',
            color: 'white',
            fontSize: '8px',
          }} title="Live">
            <i className="fa fa-circle" style={{ fontSize: '6px' }} /> LIVE
          </span>
        )}
        {txStatus.SID === TX_STATUS.Repeat && (
          <span style={{
            padding: '1.5px 2px 0px',
            backgroundColor: '#6c757d',
            color: 'white',
            fontSize: '8px',
          }} title="Repeat">R</span>
        )}
        {txStatus.SID === TX_STATUS.SDD && (
          <span style={{
            padding: '1.5px 2px 0px',
            backgroundColor: '#fd7e14',
            color: 'white',
            fontSize: '8px',
          }} title="Delayed">SDD</span>
        )}
        {txStatus.SID === TX_STATUS.Transmission && (
          <span style={{
            padding: '1.5px 2px 0px',
            backgroundColor: '#17a2b8',
            color: 'white',
            fontSize: '8px',
          }} title="TX Mode">TX</span>
        )}
        {isCatchUp && (
          <i className="fa fa-check-square" style={{ fontSize: '11px' }} title="Catchup" />
        )}
        {isBlackOut && (
          <i className="fa fa-ban" style={{ fontSize: '11px' }} title="Blackout" />
        )}
      </div>
    </div>
  );
});

// Main component - NO React.memo to ensure drag/drop works correctly
const CustomPlanningItemV2 = (props) => {
  const { onFocus } = props;

  // Tooltip state
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef(null);
  const mouseDownSelectedRef = useRef(false);

  const itemRenderPreferences = props.itemRenderPreferences ?? {};
  const columnPreference = itemRenderPreferences.columnPreference ?? PlanningHelper.getColumnPreference();
  const showFormat = itemRenderPreferences.showFormat && props?.dataItem?.mediaEpisode?.Formats?.length > 0;
  const showNoSegment = itemRenderPreferences.showNoSegment && !props?.dataItem?.Segment_id;
  const showContract = itemRenderPreferences.showContract && props?.dataItem?.MediaDealRight_id;
  const showInvalidFormat = itemRenderPreferences.showInvalidFormat ?? false;

  const wrapPlanningSlotTitle = utility.getApplicationConfigurationByKey(ApplicationConfigurationKeys.wrapPlanningSlotTitle) ?? false;

  const IsInvalidSegmentSelected = (planneditem) => {
    if (planneditem?.dataItem?.Formats?.length > 0 && planneditem?.dataItem?.segment?.SegmentType) {
      return !planneditem?.dataItem?.Formats?.some((x) => x?.SegmentType?.SegmentCount === planneditem?.dataItem?.segment?.SegmentType?.SegmentCount);
    }
    return false;
  }

  const isInvalidSegmentSelected = IsInvalidSegmentSelected(props);

  let txStatus = props.dataItem?.TXStatus ? props.dataItem?.TXStatus : { SID: TX_STATUS.Transmission, Description: 'Transmission' };
  const showRecursive = (props?.dataItem?.RecursionParentProgramSchedule_id != undefined && props?.dataItem?.RecursionParentProgramSchedule_id != "") ? true : false;
  const isChanged = props?.dataItem?.isChanged ?? false;
  const scheduleDate = new Date(props.dataItem?.ScheduleDate).setUTCHours(0, 0, 0, 0);
  const channel = props?.dataItem?.Channel;
  const modify = utility.canModify(props.lockedPlaylist, MODULE.PLANNING, channel._id, scheduleDate);
  var canModify = modify.canModify;
  const isPlanningItemLocked = props.dataItem?.isLocked ?? false;
  const isImportant = props.dataItem?.isImportant ?? false;
  const isCatchUp = props.dataItem?.IsCatchUp ?? false;
  const isBlackOut = props.dataItem?.IsBlackOut ?? false;

  // Enhanced styling with modern look
  const style = useMemo(() => ({
    ...props.style,
    letterSpacing: '0.5px',
    fontWeight: '600',
    fontSize: '11px',
    ...props.dataItem.Style,
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    pointerEvents: canModify ? 'auto' : 'none',
    overflow: 'hidden'
  }), [props.style, props.dataItem?.Style, canModify]);

  // ALL ORIGINAL LOGIC FOR uiData PRESERVED
  const uiData = useMemo(() => columnPreference.map((item = {}) => {
    if (!props?.dataItem) {
      console.error("props.dataItem is undefined", props);
      return "";
    }

    let value = item?.key
      ? props?.dataItem[item?.key]?.[item?.name]
      : props?.dataItem?.[item?.name];

    if (item.name === "EpisodeNumber") {
      value = value
        ? ((props.dataItem?.mediaEpisode?.MediaCategory?.SID ?? 0) === MEDIACATEGORIES.Movie ? "NA" : "EP " + value)
        : "";
    }
    else if (item.name === "ExhibitionNumber") {
      value = "Exhibition: " + (typeof value === "number" ? value : value?.toString() ?? "");
    }
    else if (item.name === "RepeatNumber") {
      value = "Repeat: " + (typeof value === "number" ? value : value?.toString() ?? "");
    }
    else if (item.name === "SeasonNumber") {
      let sn = props.dataItem?.mediaEpisode?.SeasonNumber;
      value = props.dataItem?.mediaEpisode?.MediaCategory?.SID === MEDIACATEGORIES.Episode ? `${sn ? `S-${sn}` : ""}` : ""
    }
    else if (item.name === "NominalDuration") {
      let finalNominalDuration = props.dataItem.customDuration ? props.dataItem.customDuration : props.dataItem.NominalDuration;
      value = utility.convertMilisecondsToStringWithFrames(finalNominalDuration);
    }
    else if (item.name === "TX Mode") {
      if (props.dataItem?.Segment_id !== null && (props.dataItem?.segments?.length > 0 || props.dataItem?.mediaEpisode?.segments?.length > 0)) {
        let currentSegment = props.dataItem?.segment ?? props.dataItem?.mediaEpisode?.segments.find((x) => x._id === props.dataItem?.Segment_id);
        let temp = currentSegment?.SegmentType?.TXMode
          ? currentSegment?.SegmentType?.TXMode?.Name
          : utility.getKeyByValue(TX_MODE, currentSegment?.SegmentType?.TXModeSID);
        value = temp ?? "No Segments";
      } else {
        value = "No Segments";
      }
    } else if (item.name == "Break Details") {
      if (props.dataItem && props.dataItem.segment && props.dataItem.Formats && props.dataItem.Formats.length > 0) {
        let validFormat = props.dataItem.Format_id
          ? props.dataItem.Formats.find((x) => x._id === props.dataItem.Format_id)
          : props.dataItem.Formats.find((x) => {
            return x?.SegmentType?.SegmentCount === props.dataItem?.segment?.SegmentType?.SegmentCount &&
              x.Channel.SID === props.dataItem.Channel.SID
          })

        if (validFormat) {
          value = `${validFormat?.Name} - ${validFormat.TotalBreakCount} BREAKS`
        }
      }
    } else if (item.name == "Title1" || item.name == "Title2") {
      value = props.dataItem?.mediaEpisode?.CustomTitle?.[item?.name] ?? "";
    } else if (item.type === COLUMNSTYPE.array) {
      value = value?.length > 0 ? (typeof value[0]?.[item.format] === "number" ? value[0][item.format] : value[0]?.[item.format]?.toString() ?? "") : "";
    } else if (item.type === COLUMNSTYPE.time) {
      value = utility.convertMilisecondsToStringWithFrames(value ?? 0);
    } else if (item.type === COLUMNSTYPE.date) {
      value = utility.convertMilisecondsToStringWithFrames(value ?? 0);
    } else if (item.name === "Language") {
      value = props.dataItem?.segment?.Language?.Language?.Description ?? "";
    } else if (item.name == "EpisodeTitle1" || item.name == "EpisodeTitle2") {
      value = props.dataItem?.mediaEpisode?.CustomEpisodeTitle?.[item?.name == "EpisodeTitle1" ? "Title1" : "Title2"] ?? "";
    } else if(item.name === "Description") {
      value = props?.dataItem?.Description ? props?.dataItem?.Description : props?.dataItem?.mediaEpisode?.Description;
    }

    return value && typeof value === "number" ? value : value?.toString() ?? "";
  }), [columnPreference, props.dataItem]);

  // Time range for tooltip header
  const startTime = useMemo(
    () => moment(props?.dataItem?.SlotDateTime).utc().format('HH:mm A'),
    [props?.dataItem?.SlotDateTime]
  );
  const endTime = useMemo(
    () => moment(props?.dataItem?.SlotDateTime + (props?.dataItem?.customDuration && props?.dataItem?.customDuration > 0 ? props?.dataItem?.customDuration : props?.dataItem?.ActualDuration)).utc().format('HH:mm A'),
    [props?.dataItem?.SlotDateTime, props?.dataItem?.customDuration, props?.dataItem?.ActualDuration]
  );
  const title = props?.dataItem?.Title;

  const handleFocus = useCallback((event) => {
    if (onFocus) {
      props.setFocusedPlanningItemPropRef(props);
      onFocus(event);
    }
  }, [onFocus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Check if any icon should show
  const hasAnyIcon = isPlanningItemLocked || isImportant || showFormat || showNoSegment ||
    showContract || showRecursive || isChanged ||
    (showInvalidFormat && isInvalidSegmentSelected) || txStatus.SID;

  // Custom tooltip content - styled like the planning item
  const renderTooltip = () => {
    if (!showTooltip) return null;

    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          maxWidth: '320px',
          maxHeight: '300px',
          backgroundColor: props?.dataItem?.Style?.backgroundColor || '#f8f9fa',
          color: props?.dataItem?.Style?.color || '#212529',
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          border: '2px solid rgba(0,0,0,0.15)',
          zIndex: 999999,
          pointerEvents: 'none',
          overflow: 'hidden',
          fontFamily: 'inherit',
          opacity: 0.95
        }}
      >
        {/* Header with time and title */}
        <div style={{
          padding: '10px 12px',
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginBottom: '4px',
            color: 'inherit'
          }}>
            {startTime} - {endTime}
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'inherit',
            wordBreak: 'break-word'
          }}>
            {title}
          </div>
        </div>

        {/* Details */}
        <div style={{
          padding: '10px 12px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {uiData.map((item, index) => {
            if (item) {
              return (
                <div
                  key={index}
                  style={{
                    fontSize: '11px',
                    padding: '3px 0',
                    borderBottom: index < uiData.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                    color: 'inherit'
                  }}
                >
                  {item}
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* Status bar */}
        {hasAnyIcon && (
          <div style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
            fontSize: '10px'
          }}>
            {isPlanningItemLocked && <span><i className="fa fa-lock" style={{ color: '#dc3545' }} /> Locked</span>}
            {isImportant && <span><i className="fa fa-exclamation-triangle" style={{ color: '#ffc107' }} /> Important</span>}
            {showFormat && <span><i className="fa fa-cube" /> Format</span>}
            {showNoSegment && <span><i className="fa fa-window-close" /> No Segments</span>}
            {showContract && <span><i className="fa fa-pencil-square" /> Contract</span>}
            {showRecursive && <span><i className="fa fa-bars" /> Recursive</span>}
            {isChanged && <span><i className="fa fa-refresh" /> Changed</span>}
            {txStatus.SID === TX_STATUS.Live && <span><i className="fa fa-circle" style={{ color: '#dc3545' }} /> LIVE</span>}
            {txStatus.SID === TX_STATUS.Repeat && <span>R - Repeat</span>}
            {txStatus.SID === TX_STATUS.SDD && <span>SDD - Delayed</span>}
            {txStatus.SID === TX_STATUS.Transmission && <span>TX - Transmission</span>}
            {isCatchUp && <span><i className="fa fa-check-square" /> Catchup</span>}
            {isBlackOut && <span><i className="fa fa-ban" /> Blackout</span>}
          </div>
        )}
      </div>,
      document.body
    );
  };

  const handleMouseEnter = useCallback((event) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    const target = event?.currentTarget?.getBoundingClientRect
      ? event.currentTarget
      : document.getElementById(`planning-item-${props.dataItem._id}`) ?? event?.target?.closest?.(".k-event");

    if (!target || typeof target.getBoundingClientRect !== "function") {
      return;
    }

    const rect = target.getBoundingClientRect();
    let x = rect.right + 10;
    let y = rect.top;

    if (x + 320 > window.innerWidth) {
      x = rect.left - 330;
    }
    if (x < 10) {
      x = Math.max(10, rect.left);
      y = rect.top - 200;
    }
    if (y + 300 > window.innerHeight) {
      y = window.innerHeight - 310;
    }
    if (y < 10) {
      y = 10;
    }

    setTooltipPosition({ x, y });
    hoverTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  }, [props.dataItem._id]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowTooltip(false);
  }, []);

  const isRightClickEvent = (event) => {
    return event?.button === 2 || event?.nativeEvent?.button === 2 || event?.which === 3 || event?.nativeEvent?.which === 3;
  };

  const handleMouseDown = useCallback((event) => {
    if (event.detail == 2) {
      return;
    }

    props.setFocusedPlanningItemPropRef(props);
    if (isRightClickEvent(event)) {
      mouseDownSelectedRef.current = false;
      return;
    }

    if (mouseDownSelectedRef.current) {
      return;
    }

    const activeTarget = event?.currentTarget?.id
      ? event.currentTarget
      : document.getElementById(`planning-item-${props.dataItem._id}`);

    props.onPlannerItemSelect(event, props.dataItem, activeTarget);
    mouseDownSelectedRef.current = true;
  }, [props]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    props.setFocusedPlanningItemPropRef(props);
    setShowTooltip(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    mouseDownSelectedRef.current = false;
  }, [props]);

  const handleClick = useCallback((event) => {
    setShowTooltip(false);

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    const activeTarget = event?.currentTarget?.id
      ? event.currentTarget
      : document.getElementById(
        `planning-item-${props.dataItem._id}`
      );

    // RIGHT CLICK
    if (isRightClickEvent(event)) {
      props.setFocusedPlanningItemPropRef(props);

      props.onPlannerItemSelect(
        event,
        props.dataItem,
        activeTarget
      );

      if (activeTarget) {
        activeTarget.style.border =
          "2px solid #212529";

        activeTarget.style.boxShadow =
          "0 2px 8px rgba(0,0,0,0.15)";
      }

      mouseDownSelectedRef.current = false;

      return;
    }

    // DOUBLE CLICK
    if (event.syntheticEvent.detail === 2) {
      if (props.onPlannerItemDoubleClick) {
        props.onPlannerItemDoubleClick(event, props.dataItem, activeTarget);
      }
    } else {
      // SINGLE CLICK

      if (!mouseDownSelectedRef.current) {
        props.setFocusedPlanningItemPropRef(props);

        props.onPlannerItemSelect(
          event,
          props.dataItem,
          activeTarget
        );
      }

      mouseDownSelectedRef.current = false;

      // props.onClick(event);

      // if (
      //   !event.syntheticEvent.ctrlKey &&
      //   !event.syntheticEvent.metaKey &&
      //   !event.syntheticEvent.shiftKey
      // ) {
      //   if (activeTarget) {
      //     activeTarget.style.border =
      //       "2px solid #212529";

      //     activeTarget.style.boxShadow =
      //       "0 2px 8px rgba(0,0,0,0.15)";
      //   }
      // }
    }
  }, [props]);

  // Helper to update the Title text inside Kendo's props.children while keeping overflow arrows (◀ / ▶)
  const renderModifiedChildren = () => {
  const children = props.children;

  const rawTitle = props.dataItem?.Title;

  const formattedTitle = wrapPlanningSlotTitle ? (typeof rawTitle === "string" && rawTitle.length > 25
      ? rawTitle.substring(0, 22) + "..."
      : rawTitle) : rawTitle;

  if (!formattedTitle) {
    return children;
  }

  return React.Children.map(children, (child) => {
    // String child
    if (typeof child === "string") {
      return formattedTitle;
    }

    // Not a React element
    if (!React.isValidElement(child)) {
      return child;
    }

    const inner = child.props?.children;

    // Direct string inside element
    if (typeof inner === "string") {
      return React.cloneElement(child, {}, formattedTitle);
    }

    // Array of children
    if (Array.isArray(inner)) {
      const updatedArray = inner.map((innerChild) => {
        if (typeof innerChild === "string") {
          return formattedTitle;
        }

        if (
          React.isValidElement(innerChild) &&
          typeof innerChild.props?.children === "string"
        ) {
          return React.cloneElement(innerChild, {}, formattedTitle);
        }

        return innerChild;
      });

      return React.cloneElement(child, {}, updatedArray);
    }

    // Nested React element
    if (
      React.isValidElement(inner) &&
      typeof inner.props?.children === "string"
    ) {
      return React.cloneElement(
        child,
        {},
        React.cloneElement(inner, {}, formattedTitle)
      );
    }

    return child;
  });
};

  return (
    <>
      <SchedulerItem {...props}
        id={`planning-item-${props.dataItem._id}`}
        style={style}
        onFocus={handleFocus}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDownCapture={handleMouseDown}
        onPointerDownCapture={handleMouseDown}
        onContextMenu={handleContextMenu}
        onClick={handleClick}
      >
        {/* Render Kendo default children with modified Title while keeping overflow arrows (◀ / ▶) */}
        {renderModifiedChildren()}
        <SchedulerItemContent>
          <div style={{
            padding: '4px 2px',
            paddingBottom: hasAnyIcon ? '24px' : '4px',
            lineHeight: '1.3'
          }}>
            {uiData.map((item, index) => {
              if (item) {
                return (
                  <div
                    key={index + "-" + props.dataItem?._id}
                    className='customItem'
                    style={{
                      marginBottom: '2px',

                      // REMOVED truncated text styles to allow full content display

                      // whiteSpace: 'nowrap',
                      // overflow: 'hidden',
                      // textOverflow: 'ellipsis'

                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item && item.length > 50 ? item.substring(0, 47) + "..." : item}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </SchedulerItemContent>

        <StatusIcons
          isPlanningItemLocked={isPlanningItemLocked}
          isImportant={isImportant}
          showFormat={showFormat}
          showNoSegment={showNoSegment}
          showContract={showContract}
          showRecursive={showRecursive}
          showInvalidFormat={showInvalidFormat}
          isInvalidSegmentSelected={isInvalidSegmentSelected}
          isChanged={isChanged}
          txStatus={txStatus}
          hasAnyIcon={hasAnyIcon}
          backgroundColor={props?.dataItem?.Style?.backgroundColor}
          isCatchUp={isCatchUp}
          isBlackOut={isBlackOut}
        />
      </SchedulerItem>

      {/* Custom tooltip rendered via Portal */}
      {renderTooltip()}
    </>
  );
};

export default CustomPlanningItemV2;
